import { type CardProps, Card } from 'antd';
import i18n from 'i18next';
import type { ReactNode } from 'react';

import {
  atlasPosition,
  make as bladeMake,
  model as bladeModel,
  serial,
} from 'components/data/blades';
import {
  make as genericMake,
  model as genericModel,
  position as genericPosition,
  serial as genericSerial,
} from 'components/data/generic';
import { name as locationName } from 'components/data/locations';
import {
  atlasLocation,
  make,
  model,
  name as turbineName,
  serial as turbineSerial,
  status,
} from 'components/data/turbines';
import { DataDescription } from 'components/DataDescription';
import { WORK_ORDER_COLLABORATOR } from 'utils/access-control/roles';
import { getAssetPosition } from 'utils/asset-helpers';
import {
  ASSET_TYPE_BLADE,
  ASSET_TYPE_GENERIC,
  ASSET_TYPE_LOCATION,
  ASSET_TYPE_SITE,
  ASSET_TYPE_TURBINE,
} from 'utils/constants';
import { usePermissions } from 'utils/usePermissions';

import { removeAssetLink } from './helpers';

type Props = {
  assetData: any;
  assetType: string;
  size?: CardProps['size'];
  extra?: ReactNode;
};

export const AssetDetailCard = ({ assetData, assetType, size, extra }: Props) => {
  const { hasRole } = usePermissions();
  const isWoCollaborator = hasRole(WORK_ORDER_COLLABORATOR);

  /* work order collaborators need to have the asset links scrubbed off so they don't click into
     asset details pages that they shouldn't have access to
  */
  const cleanLocationName = isWoCollaborator ? removeAssetLink(locationName) : locationName;
  const locationColumns = [{ ...cleanLocationName, title: i18n.t('table.site', 'Site') }];
  const turbineColumns = [
    isWoCollaborator ? removeAssetLink(turbineName) : turbineName,
    isWoCollaborator ? removeAssetLink(atlasLocation) : atlasLocation,
    turbineSerial,
    status,
    make,
    model,
  ];
  const bladeColumns = [
    atlasPosition,
    isWoCollaborator ? removeAssetLink(serial) : serial,
    bladeMake,
    bladeModel,
  ];

  const genericColumns = [
    isWoCollaborator ? removeAssetLink(genericSerial) : genericSerial,
    genericMake,
    genericModel,
  ];

  const position = getAssetPosition(assetData);
  if (position) {
    genericColumns.unshift(genericPosition);
  }

  const attrMap = {
    [ASSET_TYPE_LOCATION]: locationColumns,
    [ASSET_TYPE_SITE]: locationColumns,
    [ASSET_TYPE_TURBINE]: turbineColumns,
    [ASSET_TYPE_BLADE]: bladeColumns,
    [ASSET_TYPE_GENERIC]: genericColumns,
  };

  const attributes = attrMap[assetType as keyof typeof attrMap];

  const getTranslatedAssetTypeName = (type: string, assetData: any) => {
    switch (type) {
      case ASSET_TYPE_TURBINE:
        return i18n.t('table.turbineDetails', 'Turbine Details');
      case ASSET_TYPE_BLADE:
        return i18n.t('table.bladeDetails', 'Blade Details');
      case ASSET_TYPE_LOCATION:
        return i18n.t('table.locationDetails', 'Location Details');
      case ASSET_TYPE_SITE:
        return i18n.t('table.siteDetails', 'Site Details');
      case ASSET_TYPE_GENERIC:
        return assetData?.assetType?.name
          ? i18n.t('table.typeDetails', '{{type}} Details', {
              type: assetData.assetType.name,
            })
          : i18n.t('table.componentDetails', 'Component Details');
      default:
        return i18n.t('table.typeDetails', '{{type}} Details', { type });
    }
  };

  const assetTypeName = getTranslatedAssetTypeName(assetType, assetData);

  return (
    <Card
      size={size}
      title={assetTypeName}
      extra={extra}
      data-testid={`${assetType.toLowerCase()}-details-card`}
    >
      <DataDescription attributes={attributes} data={assetData} loading={false} />
    </Card>
  );
};
